<script setup>
import { ref, computed, onBeforeMount, onMounted, getCurrentInstance } from 'vue'
import store from '@/store'
import { RESOLVER_URL, SERVICES_URL, STDIZER_URL } from '@/main'
import ReportControls from '@/components/ReportControls.vue'
import StdizerProtocolToolbar from '@/components/stdizer/StdizerProtocolToolbar.vue'
import { defaultStdizerAppearancePreferences, stdizerOptionsPreference } from '@/components/stdizer/stdizerPreferences'
import TableCardLayout from '@/components/report-page-layouts/TableCardLayout.vue'
import TableControls from '@/components/report-page-layouts/TableControls.vue'
import ProtocolView from '@/components/stdizer/ProtocolView.vue'
// import LookupChemicals from '@/components/lookup/LookupChemicals.vue'
import { defaultVTooltipProps } from '@/utils/defaultProps'
import { showErrorToast } from '@/utils/toast'

const proxy = getCurrentInstance().proxy

const status = ref(null)
const workflows = ref([])
const options = stdizerOptionsPreference
const protocolToolbarController = ref(null)
const fallbackProtocolToolbarController = {
  protocol: null,
  search: null,
  issue: null,
  issuesLoading: false,
  issues: [],
  change: null,
  changesLoading: false,
  changes: [],
  recordIdField: defaultStdizerAppearancePreferences.recordIdField,
  recordIdOptions: [
    { value: 'cid', text: 'DTXCID' },
    { value: 'sid', text: 'DTXSID' },
    { value: 'name', text: 'Name' },
    { value: 'casrn', text: 'CASRN' },
    { value: 'smiles', text: 'SMILES' },
    { value: 'inchi', text: 'InChI' },
    { value: 'inchiKey', text: 'InChIKey' },
  ],
  countHidden: 0,
  hidden: false,
  countComponents: 0,
  components: false,
  countChanged: 0,
  changed: false,
  countFailed: 0,
  failed: false,
  countPassed: 0,
  passed: false,
  statuses: [],
  curationFilter: [],
  statusFilterChanged: () => {},
  isAuthenticated: false,
  curateMode: false,
  selectable: false,
  showDetails: defaultStdizerAppearancePreferences.showDetails,
  visibleCurate: false,
  selected: [],
  filteredItems: [],
  stdizerExportItems: [],
  STDIZER_URL,
}

const getCheckedStructures = computed(() => store.getters.getCheckedStructures)
const isAdmin = computed(() => store.getters.isAdmin)
const isAuthenticated = computed(() => store.getters.isAuthenticated)
const stdizerResult = computed(() => store.getters.stdizerResult)

const hasValidReportInputs = computed(() => Boolean(getCheckedStructures.value?.length))
const hasProtocol = computed(() => !!stdizerResult.value)
const toolbarController = computed(() => protocolToolbarController.value || fallbackProtocolToolbarController)

const reportControlsStatus = computed(() => {
  if (status.value === 'PENDING') return 'loading'
  if (hasProtocol.value) return hasValidReportInputs.value ? 'stale' : 'fresh'
  return hasValidReportInputs.value ? 'ready' : 'invalid'
})

const onClearReportControls = () => {
  status.value = null
  protocolToolbarController.value = null
  store.commit('resetStdizerResult')
}

const onStdize = (chemicals) => {
  store.commit('SET_VIEW_STRUCTURES', false)
  status.value = 'PENDING'

  proxy.$axios
    .post(`${STDIZER_URL}/chemicals`, {
      chemicals: Array.isArray(chemicals) ? chemicals : getCheckedStructures.value,
      options: options.value,
      full: true,
    })
    .then((response) => {
      status.value = 'DONE'
      store.commit('setStdizerResult', response.data)
    })
    .catch((err) => {
      console.error(err)
      status.value = 'ERROR'
      showErrorToast(err)
    })
}

const onFilesUploaded = (result) => store.commit('setStdizerResult', result[0])

const getWorkflows = () => {
  proxy.$axios.get(`${STDIZER_URL}/workflows`).then((response) => {
    workflows.value = response.data
  })
}

const onProtocolViewReady = (controller) => {
  protocolToolbarController.value = controller
}

const maybeOpenChemCart = () => {
  if (!hasValidReportInputs.value && !hasProtocol.value) store.commit('SET_VIEW_STRUCTURES', true)
}

onBeforeMount(() => {
  status.value = stdizerResult.value?.records?.length ? 'DONE' : null
})

onMounted(() => {
  getWorkflows()
  maybeOpenChemCart()
})
</script>

<template>
  <div class="d-flex flex-column h-100">
    <TableCardLayout>
      <template #table-controls>
        <TableControls :loading="status === 'PENDING'">
          <template #left>
            <b-form-select
              id="stdizer-workflow-select"
              v-model="options.workflow"
              :disabled="!hasValidReportInputs"
              :options="workflows"
              value-field="name"
              text-field="title"
              class="stdizer-toolbar__workflow"
            />
            <v-tooltip
              top
              v-bind="defaultVTooltipProps"
              activator="#stdizer-workflow-select"
            >
              Standardizer workflows
            </v-tooltip>

            <ReportControls
              :status="reportControlsStatus"
              calculate-id="stdizer-standardize-button"
              clear-id="stdizer-clear-button"
              calculate-tooltip="Standardize chemicals in ChemCart"
              calculate-aria-label="Standardize chemicals in ChemCart"
              clear-aria-label="Clear standardization protocol"
              :ui="{ group: '' }"
              @calculate="onStdize"
              @clear="onClearReportControls"
            />
          </template>

          <template
            v-if="hasProtocol"
            #right
          >
            <StdizerProtocolToolbar :controller="toolbarController" />

            <v-btn-toggle
              v-if="isAdmin || isAuthenticated"
              dense
            >
              <v-btn
                v-if="isAuthenticated"
                id="stdizer-protocols-button"
                to="/stdizer/protocols"
                class="no-link-under"
                aria-label="Standardization protocols"
              >
                <v-icon>mdi-table-heart</v-icon>
              </v-btn>
              <v-tooltip
                top
                v-bind="defaultVTooltipProps"
                activator="#stdizer-protocols-button"
              >
                Standardization protocols
              </v-tooltip>

              <v-btn
                v-if="isAdmin"
                id="stdizer-groups-button"
                to="/stdizer/groups"
                class="no-link-under"
                aria-label="Workflows definitions"
              >
                <v-icon>mdi-database</v-icon>
              </v-btn>
              <v-tooltip
                top
                v-bind="defaultVTooltipProps"
                activator="#stdizer-groups-button"
              >
                Workflows definitions
              </v-tooltip>
            </v-btn-toggle>
          </template>
        </TableControls>
      </template>

      <ProtocolView
        v-if="hasProtocol"
        inline-mode
        @ready="onProtocolViewReady"
        class="h-100 w-100"
      />
    </TableCardLayout>
  </div>
</template>
<style scoped>
.stdizer-toolbar__workflow {
  min-width: 12rem;
  max-width: 18rem;
}
</style>
